/* iter100.157s — Premium toast (Linear / Vercel-style).
 *
 * Ultra-clean white surface, tight typography, a small coloured status
 * chip housing the icon. No pastel background wash. No heavy shadow.
 * Reads as polished infrastructure UI, not a decorated card.
 *
 * All app code keeps calling `toast.success(...)` / `toast.error(...)`.
 */
import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";
import {
    CheckCircle, XCircle, WarningCircle, Info,
} from "@phosphor-icons/react";


/*  Coloured status chip — small circular badge that houses the icon.
    Semantic weight is carried by the icon color + a whisper of matching
    tint behind it. Sits flush with the toast title. */
const StatusChip = ({ tone, Icon }) => {
    const map = {
        emerald: "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100",
        rose:    "bg-rose-50 text-rose-600 ring-1 ring-rose-100",
        amber:   "bg-amber-50 text-amber-600 ring-1 ring-amber-100",
        sky:     "bg-sky-50 text-sky-600 ring-1 ring-sky-100",
    };
    return (
        <span className={`inline-grid place-items-center w-7 h-7 rounded-full shrink-0 ${map[tone]}`}>
            <Icon size={15} weight="fill" />
        </span>
    );
};


const Toaster = ({ ...props }) => {
    const { theme = "system" } = useTheme();

    return (
        <Sonner
            theme={theme}
            position="top-center"
            duration={3800}
            visibleToasts={4}
            gap={8}
            className="toaster group"
            toastOptions={{
                icons: {
                    success: <StatusChip tone="emerald" Icon={CheckCircle} />,
                    error:   <StatusChip tone="rose" Icon={XCircle} />,
                    warning: <StatusChip tone="amber" Icon={WarningCircle} />,
                    info:    <StatusChip tone="sky" Icon={Info} />,
                },
                classNames: {
                    /* Base card — solid white surface, thin border, subtle
                       shadow. Rounded-xl (crisp corners, not soft blob).
                       Compact padding + tight max-width for that "system
                       notification" feel. */
                    toast: [
                        "group toast",
                        "!rounded-xl !border !border-slate-200",
                        "!bg-white",
                        "!p-3 !pr-3.5 !gap-3 !items-center",
                        "min-w-[300px] max-w-[420px]",
                        "!shadow-[0_8px_28px_-8px_rgba(15,23,42,0.14),0_2px_6px_-2px_rgba(15,23,42,0.06)]",
                    ].join(" "),

                    title: [
                        "!text-[13px] !font-semibold !leading-tight !tracking-tight",
                        "!text-slate-900",
                    ].join(" "),

                    description: [
                        "!text-[12px] !mt-1 !leading-snug",
                        "!text-slate-500",
                    ].join(" "),

                    icon: "!m-0 !p-0 !shrink-0",

                    actionButton: [
                        "!rounded-md !px-2.5 !py-1 !text-[12px] !font-semibold",
                        "!bg-slate-900 hover:!bg-slate-800 !text-white",
                    ].join(" "),

                    cancelButton: [
                        "!rounded-md !px-2.5 !py-1 !text-[12px] !font-medium",
                        "!bg-transparent hover:!bg-slate-100 !text-slate-500",
                    ].join(" "),

                    closeButton: [
                        "!top-2.5 !right-2.5 !w-5 !h-5",
                        "!bg-transparent hover:!bg-slate-100 !border-0",
                        "!text-slate-400",
                    ].join(" "),
                },
            }}
            {...props}
        />
    );
};

export { Toaster, toast };
